import {Component, OnInit} from '@angular/core';
import {LogService} from '../services/log.service';
import {CommonModule} from '@angular/common';
import {NzTableModule} from 'ng-zorro-antd/table';
import {NzPaginationModule} from 'ng-zorro-antd/pagination';
import {NzMessageService} from 'ng-zorro-antd/message';
import {NzInputModule} from 'ng-zorro-antd/input';
import {FormsModule} from '@angular/forms';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzIconModule} from 'ng-zorro-antd/icon';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-log-management',
  standalone: true,
  imports: [
    CommonModule,
    NzTableModule,
    NzPaginationModule,
    NzInputModule,
    FormsModule,
    NzButtonModule,
    NzIconModule
  ],
  templateUrl: './log-management.component.html',
  styleUrl: './log-management.component.less'
})
export class LogManagementComponent implements OnInit{
  allLogs: any[] = [];
  logs: any[] = [];
  logsPagination = {
    pageIndex: 1,
    pageSize: 10,
    total: 0
  };
  logsSort: { key: string; value: string | null } = { key: '', value: null };
  isLoading = false;

  searchEmail: string = ''; // New search field

  constructor(private logService: LogService, private message: NzMessageService) {}

  ngOnInit(): void {
    this.loadLogs();
  }

  loadLogs(): void {
    this.isLoading = true;
    this.logService.getLogs().subscribe(
      (logs) => {
        this.allLogs = logs;
        this.logsPagination.total = this.allLogs.length;
        this.applyLogsPagination();
        this.isLoading = false;
      },
      (error) => {
        this.message.error('Failed to load logs');
        this.isLoading = false;
      }
    );
  }

  applyLogsPagination(): void {
    const start = (this.logsPagination.pageIndex - 1) * this.logsPagination.pageSize;
    const end = this.logsPagination.pageIndex * this.logsPagination.pageSize;

    // Only apply email search filter if searchEmail is not empty
    let filteredLogs = this.allLogs;

    if (this.searchEmail.trim()) {
      filteredLogs = this.allLogs.filter((log) =>
        log.userEmail?.toLowerCase().includes(this.searchEmail.toLowerCase())
      );
    }

    this.logsPagination.total = filteredLogs.length; // Update total count after filtering
    this.logs = filteredLogs.slice(start, end); // Apply pagination to filtered logs
  }

  applyLogsSorting(): void {
    if (this.logsSort.key && this.logsSort.value) {
      const sortOrder = this.logsSort.value === 'ascend' ? 1 : -1;
      this.allLogs.sort((a, b) => {
        let aVal = '';
        let bVal = '';

        // Handle different fields including userEmail
        if (this.logsSort.key === 'user') {
          // Sorting based on userEmail, handle case where it's missing
          aVal = a.userEmail?.toString().toLowerCase() || '';
          bVal = b.userEmail?.toString().toLowerCase() || '';
        } else {
          // Sorting for other keys
          aVal = a[this.logsSort.key]?.toString().toLowerCase() || '';
          bVal = b[this.logsSort.key]?.toString().toLowerCase() || '';
        }

        return aVal < bVal ? -1 * sortOrder : aVal > bVal ? 1 * sortOrder : 0;
      });
    }
  }

  // Handle pagination changes
  onLogsPageIndexChange(pageIndex: number): void {
    this.logsPagination.pageIndex = pageIndex;
    this.applyLogsPagination();
  }

  onLogsPageSizeChange(pageSize: number): void {
    this.logsPagination.pageSize = pageSize;
    this.logsPagination.pageIndex = 1; // Reset to first page when changing page size
    this.applyLogsPagination();
  }

  // Handle sorting changes
  onLogsSortChange(key: string, value: any): void {
    this.logsSort = { key, value };
    this.applyLogsSorting();
    this.applyLogsPagination(); // Adjust pagination after sorting
  }

  onSearchChange(): void {
    this.logsPagination.pageIndex = 1; // Reset to first page when searching
    this.applyLogsPagination();
  }

  downloadLogsAsExcel(): void {
    // Transform the logs data: remove `__v` and format the `timestamp`
    const transformedLogs = this.allLogs.map(log => {
      const { __v, timestamp, ...rest } = log;
      return {
        ...rest,
        timestamp: new Date(timestamp).toLocaleString() // Convert the timestamp to a human-readable format
      };
    });

    // Convert the transformed logs data to a worksheet
    const worksheet = XLSX.utils.json_to_sheet(transformedLogs);

    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Logs');

    // Generate a binary Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Save the file using FileSaver
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `Logs_${new Date().toISOString()}.xlsx`);
  }
}
